import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Alert,
  Avatar,
  Divider,
  Grid
} from '@mui/material';
import { Person as PersonIcon, Security as SecurityIcon, Badge as InfoIcon } from '@mui/icons-material';
import api from '../services/api';

const Profile = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [profileData, setProfileData] = useState({
    prefix: '',
    first_name: '',
    last_name: '',
    major: '',
    phone_number: '',
    citizen_id: '',
    line_id: '',
    email: '',
    department: '',
    position: ''
  });
  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });

  const fetchProfile = async () => {
    try {
      let endpoint = '';
      if (user.role === 'STUDENT') endpoint = '/students/profile';
      else if (user.role === 'SUPERVISOR') endpoint = '/supervisors/me';
      else if (user.role === 'CO_TEACHER') endpoint = '/co-teachers/me';
      else if (['ADMIN', 'DEAN', 'STAFF'].includes(user.role)) endpoint = '/auth/me';
      
      if (endpoint) {
        const response = await api.get(endpoint);
        setProfileData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch profile', err);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user.role]);

  const handlePasswordChange = (e: any) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const handleProfileChange = (e: any) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      return setMessage({ type: 'error', text: 'รหัสผ่านใหม่ไม่ตรงกัน' });
    }

    try {
      await api.put('/auth/change-password', {
        oldPassword: passwords.oldPassword,
        newPassword: passwords.newPassword
      });
      setMessage({ type: 'success', text: 'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว' });
      setPasswords({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
        let endpoint = '';
        if (user.role === 'STUDENT') endpoint = '/students/profile';
        else if (user.role === 'SUPERVISOR') endpoint = '/supervisors/me';
        else if (user.role === 'CO_TEACHER') endpoint = '/co-teachers/me';
        else if (['ADMIN', 'DEAN', 'STAFF'].includes(user.role)) endpoint = '/auth/me/staff';

        if (endpoint) {
            await api.put(endpoint, profileData);
            setProfileMsg({ type: 'success', text: 'อัปเดตข้อมูลส่วนตัวเรียบร้อยแล้ว' });
            fetchProfile();
        }
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: 'ไม่สามารถอัปเดตข้อมูลได้' });
    }
  };

  const showBasicInfo = ['STUDENT', 'SUPERVISOR', 'CO_TEACHER', 'ADMIN', 'DEAN', 'STAFF'].includes(user.role);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', pb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 3 }}>ข้อมูลส่วนตัวและตั้งค่าความปลอดภัย</Typography>
      
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4, display: 'flex', alignItems: 'center', borderRadius: 2, boxShadow: 2 }}>
        <Avatar sx={{ width: { xs: 50, sm: 64 }, height: { xs: 50, sm: 64 }, bgcolor: 'primary.main', mr: 2 }}>
          <PersonIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 'bold' }}>{user.username}</Typography>
          <Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>บทบาท: {user.role}</Typography>
        </Box>
      </Paper>

      <Grid container spacing={4}>
        {/* Profile Info Section */}
        {showBasicInfo && (
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%', borderRadius: 2, boxShadow: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6">ข้อมูลพื้นฐาน</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              {profileMsg.text && <Alert severity={profileMsg.type as any} sx={{ mb: 3 }}>{profileMsg.text}</Alert>}

              <form onSubmit={handleProfileSubmit}>
                <Grid container spacing={2}>
                  {user.role !== 'STUDENT' && (
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="คำนำหน้าชื่อ" name="prefix" value={profileData.prefix || ''} onChange={handleProfileChange} />
                    </Grid>
                  )}
                  <Grid size={{ xs: 6 }}>
                    <TextField 
                      fullWidth 
                      label="ชื่อ (ภาษาไทย)" 
                      name="first_name" 
                      value={profileData.first_name || ''} 
                      onChange={handleProfileChange} 
                      required 
                      disabled={user.role === 'STUDENT'}
                      helperText={user.role === 'STUDENT' ? 'ชื่อ-นามสกุลทางการอ้างอิงจากฐานข้อมูลฝ่ายทะเบียน' : ''}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField 
                      fullWidth 
                      label="นามสกุล (ภาษาไทย)" 
                      name="last_name" 
                      value={profileData.last_name || ''} 
                      onChange={handleProfileChange} 
                      required 
                      disabled={user.role === 'STUDENT'}
                      helperText={user.role === 'STUDENT' ? 'หากต้องการแก้ไขโปรดติดต่อเจ้าหน้าที่' : ''}
                    />
                  </Grid>
                  {user.role === 'STUDENT' && (
                    <Grid size={{ xs: 12 }}>
                        <TextField 
                          fullWidth 
                          label="เลขประจำตัวบัตรประชาชน" 
                          name="citizen_id" 
                          value={profileData.citizen_id || ''} 
                          onChange={handleProfileChange}
                          disabled 
                          helperText="ข้อมูลเลขประจำตัวประชาชนเชื่อมโยงจากสำนักส่งเสริมวิชาการและงานทะเบียน"
                        />
                    </Grid>
                  )}
                  {user.role === 'STUDENT' && (
                    <Grid size={{ xs: 12 }}>
                        <TextField 
                          fullWidth 
                          label="สาขาวิชา" 
                          name="major" 
                          value={profileData.major || ''} 
                          onChange={handleProfileChange} 
                          disabled
                          helperText="สาขาวิชาอ้างอิงจากฐานข้อมูลทะเบียนนักศึกษา"
                        />
                    </Grid>
                  )}
                  {user.role === 'SUPERVISOR' && (
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="สาขาวิชา/ภาควิชา" name="department" value={profileData.department || ''} onChange={handleProfileChange} />
                    </Grid>
                  )}
                  {user.role === 'CO_TEACHER' && (
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="ตำแหน่ง" name="position" value={profileData.position || ''} onChange={handleProfileChange} />
                    </Grid>
                  )}
                  <Grid size={{ xs: 6 }}>
                    <TextField fullWidth label="เบอร์โทรศัพท์" name="phone_number" value={profileData.phone_number || ''} onChange={handleProfileChange} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField fullWidth label="Line ID" name="line_id" value={profileData.line_id || ''} onChange={handleProfileChange} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth label="Email" name="email" value={profileData.email || ''} onChange={handleProfileChange} required />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Button variant="contained" type="submit" fullWidth sx={{ mt: 1 }}>บันทึกข้อมูลส่วนตัว</Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        )}

        {/* Change Password Section */}
        <Grid size={{ xs: 12, md: showBasicInfo ? 5 : 12 }}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">เปลี่ยนรหัสผ่าน</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {message.text && <Alert severity={message.type as any} sx={{ mb: 3 }}>{message.text}</Alert>}

            <form onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                type="password"
                label="รหัสผ่านปัจจุบัน"
                name="oldPassword"
                value={passwords.oldPassword}
                onChange={handlePasswordChange}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                type="password"
                label="รหัสผ่านใหม่"
                name="newPassword"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                type="password"
                label="ยืนยันรหัสผ่านใหม่"
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                sx={{ mb: 3 }}
                required
              />
              <Button variant="outlined" type="submit" fullWidth color="primary">
                อัปเดตรหัสผ่าน
              </Button>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
